import React, { useRef, useCallback, useState, useEffect } from 'react';
import api from '../services/api';
import { dedupePlaces, queryImpliesNonUs, resolveUsStateAbbr } from '../utils/usAddressRules';

export interface PlaceData {
  formatted_address: string;
  lat: number;
  lng: number;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
}

const NON_US_ADDRESS_ERROR =
  'Only United States addresses are supported. Please enter a US address.';

const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM',
  'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA',
  'WV', 'WI', 'WY',
]);

interface AddressAutocompleteProps {
  onAddressSelect: (address: string, place: PlaceData) => void;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

function isLikelyUsPlace(place?: Partial<PlaceData>): boolean {
  if (!place) return false;
  const country = String((place as PlaceData & { countrycode?: string }).countrycode || '')
    .trim()
    .toLowerCase();
  if (country === 'us' || country === 'usa') return true;
  return !!resolveUsStateAbbr(place.state);
}

async function checkUsAddress(address: string, place?: Partial<PlaceData>): Promise<boolean> {
  if (queryImpliesNonUs(address)) return false;
  if (isLikelyUsPlace(place)) return true;
  try {
    const { data } = await api.get<{ isUs?: boolean }>('/address/validate-us', {
      params: { address },
    });
    return !!data?.isUs;
  } catch {
    return false;
  }
}

async function submitUsAddress(
  address: string,
  place: PlaceData,
  onAddressSelect: AddressAutocompleteProps['onAddressSelect'],
  setUsAddressError: (message: string | null) => void,
) {
  const isUs = await checkUsAddress(address, place);
  if (!isUs) {
    setUsAddressError(NON_US_ADDRESS_ERROR);
    return;
  }
  setUsAddressError(null);
  onAddressSelect(address, place);
}

function isUsPlaceResult(place: PlaceData): boolean {
  const country = String((place as PlaceData & { countrycode?: string }).countrycode || '')
    .trim()
    .toLowerCase();
  if (country && country !== 'us' && country !== 'usa') return false;
  if (country === 'us' || country === 'usa') return true;
  return !!resolveUsStateAbbr(place.state);
}

async function searchUsAddresses(query: string): Promise<PlaceData[]> {
  if (query.length < 3 || queryImpliesNonUs(query)) return [];
  try {
    const { data } = await api.get<{ results?: PlaceData[] }>('/address/search', {
      params: { q: query, limit: 10, country: 'us' },
    });
    const results = Array.isArray(data?.results) ? data.results : [];
    return dedupePlaces(results.filter(isUsPlaceResult));
  } catch {
    return [];
  }
}

const AddressErrorAlert: React.FC<{ message: string | null }> = ({ message }) => {
  if (!message) return null;
  return (
    <div role="alert" style={errorAlertStyle}>
      {message}
    </div>
  );
};

export const formatAddressForDisplay = (
  address: string,
  parts?: Partial<PlaceData>,
) => {
  const compactParts = [parts?.street, parts?.city, parts?.state, parts?.zipCode]
    .filter((part) => part && String(part).trim())
    .map((part) => String(part).trim());
  if (compactParts.length > 0) {
    return compactParts.join(' ').replace(/\s+/g, ' ').trim();
  }
  return String(address || '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
};

function formatSuggestionSecondary(place: PlaceData): string {
  return `${formatAddressForDisplay(place.formatted_address, place)} · United States`;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  value,
  onChange,
  placeholder,
}) => {
  const [suggestions, setSuggestions] = useState<PlaceData[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [usAddressError, setUsAddressError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearch = useCallback(async (input: string) => {
    if (queryImpliesNonUs(input)) {
      setSuggestions([]);
      setShowSuggestions(false);
      setUsAddressError(NON_US_ADDRESS_ERROR);
      return;
    }

    setUsAddressError(null);
    if (input.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const results = await searchUsAddresses(input);
    setSuggestions(results);
    setShowSuggestions(results.length > 0);
    setActiveIndex(-1);
  }, []);

  const handleChange = (val: string) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(val);
    }, 350);
  };

  const handleSelect = async (place: PlaceData) => {
    const displayAddress = formatAddressForDisplay(place.formatted_address, place);
    onChange(displayAddress);
    setShowSuggestions(false);
    await submitUsAddress(displayAddress, place, onAddressSelect, setUsAddressError);
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && (activeIndex < 0 || !showSuggestions || suggestions.length === 0)) {
      e.preventDefault();
      const typed = formatAddressForDisplay(value.trim());
      if (!typed) return;
      setShowSuggestions(false);
      await submitUsAddress(
        typed,
        { formatted_address: typed, lat: 0, lng: 0 },
        onAddressSelect,
        setUsAddressError,
      );
      return;
    }

    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setShowSuggestions(true);
        }}
        placeholder={placeholder || 'Search US address only'}
        style={inputStyle}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        name="wti-us-address-search"
        aria-autocomplete="list"
      />

      <AddressErrorAlert message={usAddressError} />

      {showSuggestions && suggestions.length > 0 && (
        <div style={dropdownStyle}>
          {suggestions.map((s, i) => (
            <div
              key={`${s.lat}-${s.lng}-${s.formatted_address}`}
              style={{
                ...suggestionStyle,
                backgroundColor: i === activeIndex ? '#f0f7ff' : '#fff',
              }}
              onClick={() => handleSelect(s)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span style={{ marginRight: '10px', color: '#1a73e8', fontSize: '16px', flexShrink: 0 }}>
                &#128205;
              </span>
              <div style={{ overflow: 'hidden' }}>
                <div
                  style={{
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.street || s.formatted_address.split(' ')[0]}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#5f6368',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {formatSuggestionSecondary(s)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  fontSize: '16px',
  border: '1px solid #dadce0',
  borderRadius: '8px',
  outline: 'none',
  backgroundColor: '#fff',
  color: '#202124',
  boxSizing: 'border-box',
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: '#fff',
  border: '1px solid #dadce0',
  borderTop: 'none',
  borderRadius: '0 0 8px 8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  zIndex: 1000,
  maxHeight: '300px',
  overflowY: 'auto',
};

const suggestionStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: '14px',
  color: '#202124',
  cursor: 'pointer',
  borderBottom: '1px solid #f1f3f4',
  display: 'flex',
  alignItems: 'center',
  transition: 'background 0.15s',
};

const errorAlertStyle: React.CSSProperties = {
  marginTop: '8px',
  padding: '10px 12px',
  fontSize: '13px',
  lineHeight: 1.4,
  color: '#b3261e',
  backgroundColor: '#fce8e6',
  border: '1px solid #f9dedc',
  borderRadius: '8px',
};

export default AddressAutocomplete;
